import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Sprout, Palette, MapPin, Loader2, ArrowRight, Store,
  Home, ChevronRight, Filter, X, ArrowUpDown,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import BusinessCard from '../components/BusinessCard';
import { api } from '../services/api';

/**
 * Configuração das vitrines temáticas.
 *
 * `categorias` (slugs V2 do backend — DEVEM bater com `categoria` na coleção
 * businesses). Cada slug vira um "chip" clicável pro usuário filtrar dentro
 * do tema. `categorias_labels` mapeia slug → label pt-BR curto (badge).
 *
 * Atualizado iter207:
 *  - Economia Criativa migrada de V1 (artistas/midia/turismo/tecnologia) para
 *    V2 (artes-cultura/comunicacao-midia/turismo-hospedagem/tecnologia-telecom/
 *    eventos-festas/moda-acessorios/artesanato). Filtro V1 retornava 0.
 *  - Produtos da Roça agora combina `tipo_localizacao=comunidade_rural` OR
 *    `categoria=agronegocio` — antes só o localizacao, perdia agroindústria
 *    urbana.
 */
const TEMAS = {
  roca: {
    titulo: 'Produtos da Roça',
    subtitulo: 'Direto das comunidades rurais: queijos, doces, hortaliças, artesanato e muito mais, feitos por quem vive da terra.',
    Icon: Sprout,
    heroClass: 'bg-[#1d3a1d]',
    accentClass: 'text-lime-300',
    chipClass: 'bg-lime-300/15 text-lime-200 border border-lime-300/25',
    // Iter246: usuário quer estritamente `agronegocio` — antes o filtro
    // combinava alimentacao-bebidas e artesanato mas isso trazia negócios
    // que não são produção da roça em si (padaria, ateliê urbano). Voltamos
    // a filtrar SÓ agronegócio.
    fetchStrategies: [
      {
        params: {
          categorias: 'agronegocio',
        },
      },
    ],
    // Chips secundários de refino (visíveis dentro da vitrine).
    categorias: [
      { slug: 'agronegocio', label: 'Agronegócio' },
    ],
    vazio: 'Ainda não há produtores rurais cadastrados por aqui.',
    ctaTexto: 'É produtor rural? Cadastre seu negócio — não precisa de CEP!',
    badge: 'Roça',
  },
  criativa: {
    titulo: 'Economia Criativa',
    subtitulo: 'Arte, cultura, artesanato, moda, mídia, eventos, turismo e tecnologia: descubra quem move a criatividade da cidade.',
    Icon: Palette,
    heroClass: 'bg-[#2a0a3d]',
    accentClass: 'text-fuchsia-300',
    chipClass: 'bg-fuchsia-300/15 text-fuchsia-200 border border-fuchsia-300/25',
    fetchStrategies: [
      {
        params: {
          categorias:
            'artes-cultura,artesanato,comunicacao-midia,eventos-festas,moda-acessorios,tecnologia-telecom,turismo-hospedagem',
        },
      },
    ],
    categorias: [
      { slug: 'artes-cultura', label: 'Artes & Cultura' },
      { slug: 'artesanato', label: 'Artesanato' },
      { slug: 'moda-acessorios', label: 'Moda' },
      { slug: 'eventos-festas', label: 'Eventos' },
      { slug: 'turismo-hospedagem', label: 'Turismo' },
      { slug: 'comunicacao-midia', label: 'Mídia' },
      { slug: 'tecnologia-telecom', label: 'Tecnologia' },
    ],
    vazio: 'Ainda não há negócios criativos cadastrados por aqui.',
    ctaTexto: 'Trabalha com arte, cultura ou criatividade? Cadastre seu negócio!',
    badge: 'Criativo',
  },
};

const SORT_OPTIONS = [
  { key: 'recente', label: 'Mais recentes' },
  { key: 'alfa', label: 'Nome A-Z' },
];

export default function VitrineTematica({ tema = 'roca' }) {
  const hardcoded = TEMAS[tema] || TEMAS.roca;
  const { municipioSlug } = useParams();
  const [dbCfg, setDbCfg] = useState(null); // config vinda da API (iter247)
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState('todos');
  const [sortBy, setSortBy] = useState('recente');

  // Merge: config do DB (se existir) tem prioridade; senão cai no hardcoded.
  // Slugs armazenados no DB seguem convenção pt-br ("produtos-da-roca"),
  // enquanto tema aqui é curto ("roca"). Tentamos ambos.
  useEffect(() => {
    const dbSlug = tema === 'roca' ? 'produtos-da-roca' : tema;
    api.get(`/vitrines/${dbSlug}`).then((r) => setDbCfg(r.data)).catch(() => setDbCfg(null));
  }, [tema]);

  // Efetivo: se veio do DB usa; se não, mantém o hardcoded.
  const cfg = useMemo(() => {
    if (!dbCfg) return hardcoded;
    const paramKey = dbCfg.filter_type || 'categorias';
    const paramVal = (dbCfg.filter_values || []).join(',');
    return {
      ...hardcoded,
      titulo: dbCfg.titulo || hardcoded.titulo,
      subtitulo: dbCfg.subtitulo || hardcoded.subtitulo,
      badge: dbCfg.badge || hardcoded.badge,
      ctaTexto: dbCfg.cta_texto || hardcoded.ctaTexto,
      accentClass: dbCfg.accent || hardcoded.accentClass,
      heroClass: dbCfg.hero_bg ? undefined : hardcoded.heroClass,
      heroStyle: dbCfg.hero_bg ? { background: dbCfg.hero_bg } : undefined,
      fetchStrategies: paramVal ? [{ params: { [paramKey]: paramVal } }] : hardcoded.fetchStrategies,
    };
  }, [dbCfg, hardcoded]);

  // Carrega negócios mergiando estratégias (dedup por id).
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const seen = new Set();
      const merged = [];
      for (const strategy of cfg.fetchStrategies) {
        const params = { ...strategy.params, limit: 100 };
        if (municipioSlug) params.municipio_slug = municipioSlug;
        const res = await api.get('/businesses', { params });
        for (const b of (res.data || [])) {
          const key = b.id || b._id;
          if (key && !seen.has(key)) {
            seen.add(key);
            merged.push(b);
          }
        }
      }
      setBusinesses(merged);
    } catch {
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, [municipioSlug, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Aplica filtro por categoria + ordenação. Feito em memória para responder
  // instantaneamente aos chips sem re-fetch.
  const filteredAndSorted = useMemo(() => {
    let list = businesses;
    if (selectedCat !== 'todos') {
      list = list.filter((b) => (b.categoria || '').toLowerCase() === selectedCat);
    }
    const copy = [...list];
    if (sortBy === 'alfa') {
      copy.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
    } else {
      copy.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
    return copy;
  }, [businesses, selectedCat, sortBy]);

  // Contagem por chip pra mostrar (N) em cada botão. Calculado sobre a lista
  // COMPLETA (não filtrada), assim usuário vê sempre quantos há em cada cat.
  const countByCat = useMemo(() => {
    const out = { todos: businesses.length };
    for (const c of cfg.categorias) out[c.slug] = 0;
    for (const b of businesses) {
      const cat = (b.categoria || '').toLowerCase();
      if (cat in out) out[cat] += 1;
    }
    return out;
  }, [businesses, cfg.categorias]);

  const { Icon } = cfg;
  const cadastrarPath = municipioSlug ? `/${municipioSlug}/cadastrar` : '/cadastrar';
  const homePath = municipioSlug ? `/${municipioSlug}` : '/';

  return (
    <div className="min-h-screen bg-[var(--bg-page,#faf9f7)]" data-testid={`vitrine-${tema}`}>
      <Helmet>
        <title>{cfg.titulo} | Empreende Comunidade</title>
        <meta name="description" content={cfg.subtitulo} />
      </Helmet>

      {/* Hero */}
      <div className={`${cfg.heroClass} text-white`}>
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-white/70 mb-4" aria-label="Breadcrumb">
            <Link to={homePath} className="hover:text-white flex items-center gap-1 transition-colors">
              <Home className="w-3.5 h-3.5" /> Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white/90">{cfg.titulo}</span>
          </nav>

          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${cfg.chipClass}`}>
            <Icon className="w-3.5 h-3.5" /> Vitrine temática
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-2xl">
            {cfg.titulo}
          </h1>
          <p className="mt-3 text-white/85 max-w-2xl text-sm sm:text-base">{cfg.subtitulo}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3 sm:gap-4">
            <span className={`text-sm font-medium ${cfg.accentClass}`} data-testid="vitrine-contador">
              {loading
                ? 'Carregando...'
                : `${filteredAndSorted.length} ${filteredAndSorted.length === 1 ? 'negócio' : 'negócios'}`}
            </span>
            {municipioSlug && (
              <span className="inline-flex items-center gap-1 text-sm text-white/70 capitalize">
                <MapPin className="w-4 h-4" /> {municipioSlug.replace(/-/g, ' ')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Toolbar: chips de filtro + ordenação */}
      {!loading && businesses.length > 0 && (
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Chips (scroll horizontal em mobile) */}
            <div className="flex-1 flex items-center gap-2 overflow-x-auto pb-1 -mb-1" data-testid="vitrine-chips">
              <button
                onClick={() => setSelectedCat('todos')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCat === 'todos'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="vitrine-chip-todos"
              >
                <Filter className="w-3.5 h-3.5" />
                Todos <span className="opacity-70">({countByCat.todos})</span>
              </button>
              {cfg.categorias.map((c) => {
                const n = countByCat[c.slug] || 0;
                const active = selectedCat === c.slug;
                return (
                  <button
                    key={c.slug}
                    onClick={() => setSelectedCat(active ? 'todos' : c.slug)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      active
                        ? 'bg-gray-900 text-white'
                        : n === 0
                          ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    disabled={n === 0 && !active}
                    data-testid={`vitrine-chip-${c.slug}`}
                  >
                    {c.label} <span className="opacity-70">({n})</span>
                    {active && <X className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>

            {/* Sort dropdown (nativo em mobile) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <ArrowUpDown className="w-4 h-4 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm bg-transparent border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                data-testid="vitrine-sort"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-testid="vitrine-vazia">
            <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {selectedCat === 'todos'
                ? cfg.vazio
                : `Nenhum negócio encontrado nesta categoria. Tente outra ou veja todos.`}
            </p>
            {selectedCat !== 'todos' && (
              <button
                onClick={() => setSelectedCat('todos')}
                className="mt-3 text-sm text-[var(--primary)] font-medium hover:underline"
              >
                Ver todos os {cfg.titulo.toLowerCase()}
              </button>
            )}
            <Link
              to={cadastrarPath}
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[var(--primary)] text-white rounded-full font-medium text-sm"
            >
              Cadastrar meu negócio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="vitrine-grid">
            {filteredAndSorted.map((b, idx) => (
              <BusinessCard key={`${b.id || idx}`} business={b} variant="grid" />
            ))}
          </div>
        )}

        {/* CTA */}
        {!loading && filteredAndSorted.length > 0 && (
          <div className={`mt-10 ${cfg.heroClass} rounded-2xl p-6 sm:p-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`}>
            <div className="flex items-center gap-3">
              <Icon className={`w-8 h-8 ${cfg.accentClass}`} />
              <p className="font-medium">{cfg.ctaTexto}</p>
            </div>
            <Link
              to={cadastrarPath}
              className="px-5 py-2.5 bg-white text-gray-900 rounded-full font-semibold text-sm whitespace-nowrap hover:opacity-90"
              data-testid="vitrine-cta-cadastrar"
            >
              Cadastrar agora
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
